/**
 * Connect MT5 — the single trading-access surface.
 *
 * ONE control, several faces. The server decides which
 * (`GET /api/trading/access` → `action`), so this component never inspects the
 * role: a user who is not a trader gets the request form, a trader with a
 * ready terminal gets the login form, and so on. Keeping that decision
 * server-side means the UI cannot disagree with what the API will allow.
 *
 * The password field is the only place in the app that takes a broker
 * credential. It is posted once and never stored — not in Redux, not in
 * localStorage, not on the server (MT5 keeps the session itself). The local
 * state is cleared as soon as the request resolves.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/api/client";
import { useAppDispatch } from "@/state/hooks";
import { fetchMe } from "@/state/slices/meSlice";
import { pushToast } from "@/state/slices/notificationsSlice";
import { clearTrading } from "@/state/slices/tradingSlice";
import type { TraderAccess as Access, ServerList } from "@/api/types";
import styles from "./TraderAccess.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
}

// How long to keep waiting on a terminal that is still starting, and how
// often to re-ask. Sized from the measured worst case: a cold terminal that
// has an MT5 self-update to clear has taken minutes, while the ordinary case
// is seconds. Giving up early would leave a first-time trader with an error
// for something that was about to work.
const START_WAIT_MS = 3 * 60 * 1000;
const START_POLL_MS = 4000;

export function TraderAccess({ open, onClose }: Props) {
  const dispatch = useAppDispatch();
  const [access, setAccess] = useState<Access | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set while the trader's MT5 terminal is still coming up. A cold start is
  // slow (launch, MQL5 recompile, LiveUpdate check, broker handshake) and it
  // is NOT a failure, so it gets a waiting state rather than the red error
  // text a first-time trader would read as "it broke".
  const [starting, setStarting] = useState(false);

  // Request form
  const [broker, setBroker] = useState("");
  const [note, setNote] = useState("");

  // Login form. `password` lives here and nowhere else.
  const [server, setServer] = useState("");
  const [customServer, setCustomServer] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [demoOnly, setDemoOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAccess(await api.traderAccess());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load trading access");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  // Clear the credential whenever the dialog closes, so it never outlives the
  // interaction that needed it.
  useEffect(() => {
    if (!open) setPassword("");
  }, [open]);

  const servers: ServerList | undefined = access?.servers;

  const serverOptions = useMemo(() => {
    if (!servers) return [];
    return servers.brokers.map((b) => ({
      name: b.name,
      servers: demoOnly
        ? b.servers.filter((s) => /trial|demo/i.test(s))
        : b.servers,
    }));
  }, [servers, demoOnly]);

  const effectiveServer = server === "__custom__" ? customServer.trim() : server;

  const submitRequest = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.traderRequestAccess({ broker, note });
      setAccess(res);
      dispatch(
        pushToast({
          kind: "success",
          title: "Request sent",
          message: "You'll be notified here when it's reviewed.",
        }),
      );
      // Close on success: there is nothing more to do here, and leaving the
      // dialog open on a "pending" panel invites the user to sit and wait for
      // something that will arrive as a notification, possibly hours later.
      // The toast carries the confirmation out with them.
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not send the request");
    } finally {
      setBusy(false);
    }
  };

  const submitConnect = async () => {
    const acct = Number(login.trim());
    if (!acct || acct <= 0) {
      setError("Enter your trading account number");
      return;
    }
    if (!effectiveServer) {
      setError("Choose your broker server");
      return;
    }
    setBusy(true);
    setError(null);
    setStarting(false);
    // Held locally for the duration: a retry needs it, and clearing the state
    // field mid-flight would empty the form under the user.
    const secret = password;
    const deadline = Date.now() + START_WAIT_MS;
    try {
      for (;;) {
        try {
          const res = await api.traderConnectMt5({
            login: acct,
            password: secret,
            server: effectiveServer,
          });
          setAccess(res);
          dispatch(
            pushToast({ kind: "success", title: "Trading account connected" }),
          );
          // Trading capabilities may have just become usable — refresh
          // identity so the rest of the app updates without a reload.
          void dispatch(fetchMe());
          return;
        } catch (e) {
          // `terminal_starting` means the terminal has not finished coming up.
          // The server has not counted an attempt for it, so retrying costs
          // nothing against the 5-per-15-minutes broker guard — unlike every
          // other error here, which must surface immediately.
          const isStarting =
            e instanceof ApiError && e.code === "terminal_starting";
          if (!isStarting || Date.now() >= deadline) throw e;
          setStarting(true);
          await new Promise((r) => setTimeout(r, START_POLL_MS));
        }
      }
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Could not connect to your broker",
      );
    } finally {
      setPassword("");
      setBusy(false);
      setStarting(false);
    }
  };

  const submitDisconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      setAccess(await api.traderDisconnectMt5());
      // Drop the cached account/positions/history immediately. The server has
      // logged the terminal out, but history is client-cached and the
      // account/positions survive until the next stream event — so without
      // this the trader sees their balance and closed trades still sitting
      // there after disconnecting.
      dispatch(clearTrading());
      dispatch(pushToast({ kind: "info", title: "Trading account disconnected" }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const action = access?.action ?? "request";

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Trading account access"
      >
        <header className={styles.head}>
          <h2 className={styles.title}>
            {action === "manage" ? "Your trading account" : "Log in to your trading account"}
          </h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className={styles.body}>
          {loading && <p className={styles.muted}>Loading…</p>}

          {!loading && action === "request" && (
            <>
              <p className={styles.lead}>
                Trading through TickView runs on <strong>your own</strong> broker
                account, on a dedicated trading terminal set up for you. Ask
                for access and an admin will review it.
              </p>
              <label className={styles.label}>
                Broker <span className={styles.optional}>(optional)</span>
                <input
                  className={styles.input}
                  value={broker}
                  onChange={(e) => setBroker(e.target.value)}
                  placeholder="Exness, FTMO, …"
                  maxLength={120}
                />
              </label>
              <label className={styles.label}>
                Anything we should know?{" "}
                <span className={styles.optional}>(optional)</span>
                <textarea
                  className={styles.textarea}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="What you'd like to trade, your experience…"
                />
              </label>
            </>
          )}

          {!loading && action === "pending" && (
            <div className={styles.state}>
              <div className={styles.stateIcon}>⏳</div>
              <p className={styles.lead}>
                Your request is with an admin. You'll get a notification here —
                and an email — as soon as it's reviewed.
              </p>
            </div>
          )}

          {!loading && action === "wait" && (
            <div className={styles.state}>
              <div className={styles.stateIcon}>⚙️</div>
              <p className={styles.lead}>
                Your trading terminal is being set up. This usually takes a few
                seconds — reopen this in a moment.
              </p>
            </div>
          )}

          {!loading && action === "blocked" && (
            <div className={styles.state}>
              <div className={styles.stateIcon}>🚫</div>
              <p className={styles.lead}>
                Trading access isn't available on your account.
                {access?.reject_reason ? ` ${access.reject_reason}` : ""}
              </p>
              <p className={styles.muted}>
                You can ask again if things have changed.
              </p>
            </div>
          )}

          {!loading && action === "connect" && (
            <>
              <p className={styles.lead}>
                Enter your broker account details. They go straight to your
                own dedicated terminal — <strong>TickView never stores your
                password</strong>.
              </p>

              <label className={styles.check}>
                <input
                  type="checkbox"
                  checked={demoOnly}
                  disabled={busy}
                  onChange={(e) => setDemoOnly(e.target.checked)}
                />
                Demo / trial account
              </label>

              <label className={styles.label}>
                Broker server
                <select
                  className={styles.input}
                  value={server}
                  disabled={busy}
                  onChange={(e) => setServer(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {serverOptions.map((g) => (
                    <optgroup key={g.name} label={g.name}>
                      {g.servers.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  {servers?.allow_custom && (
                    <option value="__custom__">Other…</option>
                  )}
                </select>
              </label>
              {server === "__custom__" && (
                <label className={styles.label}>
                  Server name
                  <input
                    className={styles.input}
                    value={customServer}
                    disabled={busy}
                    onChange={(e) => setCustomServer(e.target.value)}
                    placeholder="Exactly as your broker gives it"
                  />
                </label>
              )}

              <label className={styles.label}>
                Trading account number
                <input
                  className={styles.input}
                  disabled={busy}
                  value={login}
                  onChange={(e) => setLogin(e.target.value.replace(/\D/g, ""))}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="e.g. 414275230"
                />
              </label>

              <label className={styles.label}>
                Password
                <input
                  className={styles.input}
                  type="password"
                  disabled={busy}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Your trading account password"
                />
              </label>

              <p className={styles.fineprint}>
                Your broker session is held by your own trading terminal on the
                operator's machine. We'll never email you asking for this
                password.
              </p>
            </>
          )}

          {!loading && action === "manage" && (
            <div className={styles.state}>
              <div className={styles.stateIcon}>✅</div>
              <p className={styles.lead}>
                Connected to account{" "}
                <strong>{access?.mt5?.login_masked || "—"}</strong> on{" "}
                <strong>{access?.mt5?.server || "—"}</strong>.
              </p>
              <p className={styles.muted}>
                Disconnecting logs the terminal out. You'll need your password
                again to reconnect.
              </p>
            </div>
          )}

          {starting && (
            <p className={styles.waiting}>
              <span className={styles.spinner} aria-hidden="true" />
              Starting your trading terminal — this can take a minute the first
              time. Leave this open.
            </p>
          )}
          {error && !starting && <p className={styles.error}>{error}</p>}
        </div>

        <footer className={styles.foot}>
          <button className={styles.ghost} onClick={onClose} disabled={busy}>
            Close
          </button>
          {action === "request" && (
            <button className={styles.primary} onClick={submitRequest} disabled={busy}>
              {busy ? "Sending…" : "Request trading access"}
            </button>
          )}
          {action === "blocked" && (
            <button className={styles.primary} onClick={submitRequest} disabled={busy}>
              {busy ? "Sending…" : "Ask again"}
            </button>
          )}
          {action === "connect" && (
            <button
              className={styles.primary}
              onClick={submitConnect}
              disabled={busy || !password || !login || !effectiveServer}
            >
              {starting ? "Starting…" : busy ? "Connecting…" : "Connect"}
            </button>
          )}
          {action === "manage" && (
            <button className={styles.danger} onClick={submitDisconnect} disabled={busy}>
              {busy ? "Disconnecting…" : "Disconnect"}
            </button>
          )}
          {(action === "wait" || action === "pending") && (
            <button className={styles.primary} onClick={load} disabled={busy}>
              Refresh
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
